import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SvgNmrRealImag, SvgNmrOverlay3, SvgNmrOverlay3Aligned, SvgNmrAlignBottom, SvgNmrAlignCenter, } from 'cheminfo-font';
import { Fragment, useCallback, memo } from 'react';
import { FaDownload, FaFileDownload, FaFileImage, FaCopy, FaFileExport, FaFile, FaFileImport, } from 'react-icons/fa';
import { Toolbar } from 'react-science/ui';
import { useChartData } from '../context/ChartContext';
import { useDispatch } from '../context/DispatchContext';
import { useLoader } from '../context/LoaderContext';
import ToolbarMenu from '../elements/ToolbarMenu';
import { useModal } from '../elements/popup/Modal';
import useCheckExperimentalFeature from '../hooks/useCheckExperimentalFeature';
import { useCheckToolsVisibility } from '../hooks/useCheckToolsVisibility';
import useDatumWithSpectraStatistics from '../hooks/useDatumWithSpectraStatistics';
import useExport from '../hooks/useExport';
import useToolsFunctions from '../hooks/useToolsFunctions';
import { useVerticalAlign } from '../hooks/useVerticalAlign';
import ImportPublicationStringModal from '../modal/ImportPublicationStringModal';
import LoadJCAMPModal from '../modal/LoadJCAMPModal';
import { useMetaInformationImportationModal } from '../modal/metaImportation/index';
import { SET_LOADING_FLAG } from '../reducer/types/Types';
const IMPORT_MENU = [
    {
        id: 'importFile',
        icon: _jsx(FaFile, {}),
        label: 'Import from file system (Press Ctrl + O)',
    },
    {
        id: 'importJDX',
        icon: _jsx(FaFile, {}),
        label: 'Add JCAMP-DX from URL',
    },
    {
        id: 'importPublicationString',
        icon: _jsx(FaFile, {}),
        label: 'Import from publication string',
    },
    {
        id: 'importMetaInformation',
        icon: _jsx(FaFile, {}),
        label: 'Import meta information',
    },
];
const EXPORT_MENU = [
    {
        id: 'svg',
        icon: _jsx(FaDownload, {}),
        label: 'Export as SVG',
    },
    {
        id: 'png',
        icon: _jsx(FaFileImage, {}),
        label: 'Export as PNG',
    },
    {
        id: 'json',
        icon: _jsx(FaFileDownload, {}),
        label: 'Save data ( Press Ctrl + S )',
    },
    {
        id: 'advance_save',
        icon: _jsx(FaFileDownload, {}),
        label: 'Save data as  ( Press Ctrl + Shift + S )',
    },
    {
        id: 'nmre',
        icon: _jsx(FaFileDownload, {}),
        label: 'Save NMRE data',
    },
    {
        id: 'copy',
        icon: _jsx(FaCopy, {}),
        label: 'Copy image to Clipboard ( Press Ctrl + C )',
    },
];
function BasicToolBarInner({ verticalAlign, ftCounter, fidCounter, }) {
    const dispatch = useDispatch();
    const modal = useModal();
    const openLoader = useLoader();
    const openMetaInformationModal = useMetaInformationImportationModal();
    const isExperimentalFeature = useCheckExperimentalFeature();
    const isButtonVisible = useCheckToolsVisibility();
    const importMenu = isExperimentalFeature
        ? IMPORT_MENU
        : IMPORT_MENU.filter((item) => item.id !== 'importPublicationString');
    const exportMenu = isExperimentalFeature
        ? EXPORT_MENU
        : EXPORT_MENU.filter((item) => item.id !== 'nmre');
    const { isRealSpectrumShown, changeSpectrumViewHandler, changeDisplayViewModeHandler, alignSpectrumsVerticallyHandler, } = useToolsFunctions();
    const { saveAsSVGHandler, saveAsPNGHandler, saveAsJSONHandler, saveToClipboardHandler, saveAsHandler, } = useExport();
    const startLoadingHandler = useCallback(() => {
        modal.close();
        dispatch({ type: SET_LOADING_FLAG, isLoading: true });
    }, [dispatch, modal]);
    const importJCAMPFile = useCallback(() => {
        modal.show(_jsx(LoadJCAMPModal, { onClose: () => modal.close(), startLoading: startLoadingHandler }), {});
    }, [modal, startLoadingHandler]);
    const openImportPublicationStringModal = useCallback(() => {
        modal.show(_jsx(ImportPublicationStringModal, { onClose: () => modal.close() }), {});
    }, [modal]);
    const importHandler = useCallback(({ id }) => {
        switch (id) {
            case 'importFile':
                openLoader();
                break;
            case 'importJDX':
                importJCAMPFile();
                break;
            case 'importPublicationString':
                openImportPublicationStringModal();
                break;
            case 'importMetaInformation':
                openMetaInformationModal();
                break;
            default:
        }
    }, [
        openLoader,
        importJCAMPFile,
        openImportPublicationStringModal,
        openMetaInformationModal,
    ]);
    const exportHandler = useCallback(({ id }) => {
        switch (id) {
            case 'svg':
                void saveAsSVGHandler();
                break;
            case 'png':
                void saveAsPNGHandler();
                break;
            case 'json':
                saveAsJSONHandler();
                break;
            case 'advance_save':
                void saveAsHandler();
                break;
            case 'copy':
                void saveToClipboardHandler();
                break;
            default:
                break;
        }
    }, [
        saveAsSVGHandler,
        saveAsPNGHandler,
        saveAsJSONHandler,
        saveAsHandler,
        saveToClipboardHandler,
    ]);
    return (_jsxs(Fragment, { children: [isButtonVisible('import') && (_jsx(ToolbarMenu, { component: _jsx(FaFileImport, {}), toolTip: "Import", onClick: (element) => {
                    importHandler(element);
                    return null;
                }, items: importMenu })), isButtonVisible('exportAs') && (_jsx(ToolbarMenu, { component: _jsx(FaFileExport, {}), toolTip: "Export As", onClick: (element) => {
                    exportHandler(element);
                    return null;
                }, items: exportMenu })), isButtonVisible('spectraStackAlignments') && ftCounter > 1 && (_jsx(Toolbar.Item, { id: "spectra-alignment", className: "cheminfo", title: "Spectra alignment ( Press s )", onClick: changeDisplayViewModeHandler, children: verticalAlign === 'stack' ? (_jsx(SvgNmrOverlay3Aligned, {})) : (_jsx(SvgNmrOverlay3, {})) })), isButtonVisible('realImaginary') && (_jsx(Toolbar.Item, { id: "display", title: isRealSpectrumShown ? 'Display Real ' : 'Display Imaginary', onClick: changeSpectrumViewHandler, className: "cheminfo", children: _jsx(SvgNmrRealImag, {}) })), isButtonVisible('spectraCenterAlignments') &&
                (ftCounter > 0 || fidCounter > 0) && (_jsx(Toolbar.Item, { id: "baseline-position", title: verticalAlign === 'bottom'
                    ? 'Baseline  Center ( Press c )'
                    : 'Baseline  Bottom ( Press c )', onClick: alignSpectrumsVerticallyHandler, className: "cheminfo", children: _jsx("div", { style: { fontSize: 24 }, children: verticalAlign === 'bottom' ? (_jsx(SvgNmrAlignCenter, {})) : (_jsx(SvgNmrAlignBottom, {})) }) }))] }));
}
const MemoizedBasicToolBar = memo(BasicToolBarInner);
export default function BasicToolBar() {
    const { displayerMode, view: { spectra: { activeTab }, }, } = useChartData();
    const verticalAlign = useVerticalAlign();
    const { fidCounter, ftCounter } = useDatumWithSpectraStatistics();
    return (_jsx(MemoizedBasicToolBar, { ...{
            fidCounter,
            ftCounter,
            verticalAlign,
            displayerMode,
            activeTab,
        } }));
}
